/**
 * Bottle clicker SFX — Kenney (UI/glass) + BigSoundBank (PET/can), all CC0.
 * See ./sfx/LICENSE-kenney.txt
 *
 * Web Audio + master gain for instant mute.
 * Buffers are preloaded on unlock so playback isn't delayed.
 */
import catchUrl from "./sfx/catch.wav";
import canUrl from "./sfx/can.wav";
import glassUrl from "./sfx/glass.ogg";
import luckyUrl from "./sfx/lucky.wav";
import rejectUrl from "./sfx/reject.wav";
import huntUrl from "./sfx/hunt.wav";
import policeUrl from "./sfx/police.wav";
import sackUrl from "./sfx/sack.wav";
import buyUrl from "./sfx/buy.wav";
import panelOpenUrl from "./sfx/panel-open.wav";
import panelCloseUrl from "./sfx/panel-close.wav";
import printUrl from "./sfx/print.wav";
import uiUrl from "./sfx/ui.wav";
import comboUrl from "./sfx/combo.wav";

export type BottleSfxId =
  | "catch"
  | "can"
  | "glass"
  | "lucky"
  | "reject"
  | "hunt"
  | "police"
  | "sack"
  | "buy"
  | "panelOpen"
  | "panelClose"
  | "print"
  | "ui"
  | "combo";

const URLS: Record<BottleSfxId, string> = {
  catch: catchUrl,
  can: canUrl,
  glass: glassUrl,
  lucky: luckyUrl,
  reject: rejectUrl,
  hunt: huntUrl,
  police: policeUrl,
  sack: sackUrl,
  buy: buyUrl,
  panelOpen: panelOpenUrl,
  panelClose: panelCloseUrl,
  print: printUrl,
  ui: uiUrl,
  combo: comboUrl,
};

const VOLUME: Partial<Record<BottleSfxId, number>> = {
  catch: 0.2,
  can: 0.22,
  glass: 0.05,
  lucky: 0.28,
  reject: 0.18,
  hunt: 0.2,
  police: 0.18,
  sack: 0.18,
  buy: 0.2,
  panelOpen: 0.16,
  panelClose: 0.16,
  print: 0.2,
  ui: 0.14,
  combo: 0.12,
};

const ALL_IDS = Object.keys(URLS) as BottleSfxId[];

let context: AudioContext | null = null;
let master: GainNode | null = null;
let muted = false;
/** Player master level 0..1 (applied when not muted). */
let masterVolume = 0.65;
let unlocked = false;
const buffers = new Map<BottleSfxId, AudioBuffer | null>();
const loading = new Map<BottleSfxId, Promise<AudioBuffer | null>>();

function applyMasterGain() {
  if (!master || !context) return;
  const now = context.currentTime;
  master.gain.cancelScheduledValues(now);
  master.gain.setValueAtTime(muted ? 0 : masterVolume, now);
}

function getAudio(): { ctx: AudioContext; master: GainNode } | null {
  const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  context ??= new Ctor();
  if (!master) {
    master = context.createGain();
    master.gain.value = muted ? 0 : masterVolume;
    master.connect(context.destination);
  }
  return { ctx: context, master };
}

async function loadBuffer(id: BottleSfxId): Promise<AudioBuffer | null> {
  if (buffers.has(id)) return buffers.get(id) ?? null;
  const pending = loading.get(id);
  if (pending) return pending;
  const task = (async () => {
    const audio = getAudio();
    if (!audio) return null;
    try {
      const response = await fetch(URLS[id]);
      const raw = await response.arrayBuffer();
      const buffer = await audio.ctx.decodeAudioData(raw.slice(0));
      buffers.set(id, buffer);
      return buffer;
    } catch {
      buffers.set(id, null);
      return null;
    } finally {
      loading.delete(id);
    }
  })();
  loading.set(id, task);
  return task;
}

/** Call from a user gesture so mobile browsers allow later playback. */
export function unlockBottleSfx() {
  const audio = getAudio();
  if (!audio) return;
  if (audio.ctx.state === "suspended") void audio.ctx.resume();
  if (unlocked) return;
  unlocked = true;
  // Decode everything up front so catches aren't waiting on fetch.
  void Promise.all(ALL_IDS.map((id) => loadBuffer(id)));
}

export function setBottleSfxMuted(next: boolean) {
  muted = next;
  applyMasterGain();
}

/** 0 = silent, 1 = full. Does not unmute — use setBottleSfxMuted for that. */
export function setBottleSfxVolume(next: number) {
  masterVolume = Math.min(1, Math.max(0, Number.isFinite(next) ? next : masterVolume));
  applyMasterGain();
}

export function getBottleSfxVolume() {
  return masterVolume;
}

export function isBottleSfxMuted() {
  return muted;
}

export function playBottleSfx(id: BottleSfxId) {
  if (muted || masterVolume <= 0) return;
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
  const audio = getAudio();
  if (!audio) return;
  if (audio.ctx.state === "suspended") void audio.ctx.resume();

  const play = (buffer: AudioBuffer) => {
    if (muted || document.visibilityState === "hidden") return;
    try {
      const source = audio.ctx.createBufferSource();
      const gain = audio.ctx.createGain();
      gain.gain.value = VOLUME[id] ?? 0.3;
      source.buffer = buffer;
      source.connect(gain);
      gain.connect(audio.master);
      source.start(0);
    } catch {
      /* ignore */
    }
  };

  const ready = buffers.get(id);
  if (ready) {
    play(ready);
    return;
  }
  void loadBuffer(id).then((buffer) => {
    if (buffer) play(buffer);
  });
}
